/**
 * src/router.ts — roteamento por History API, sem dependência nova.
 *
 * Por que não react-router/um framework: o PLAYER é a superfície de 24/7 e não
 * ganha dependência nenhuma (HYBRID §10). Um roteador de rotas literais com
 * 5 caminhos é ~50 linhas; a biblioteca custaria bundle numa TV Box.
 *
 * O QUE ESTE ARQUIVO NÃO FAZ: ele não decide o MODO. `/tela` e `/entrar` podem
 * coexistir no mesmo carregamento — quem escolhe é `ui/app.ts` no boot
 * (HYBRID §4). Aqui só existe "path → handler" e a pilha mínima.
 *
 * Requisito de infraestrutura: o host estático precisa de fallback `/* → index.html`
 * (documentado no README). Sem isso, recarregar em `/tela` dá 404 na TV.
 */
export type RouteHandler = (path: string) => void | Promise<void>;

export interface RouterOptions {
  /** Rota inicial quando a URL não tem `pathname` aproveitável. */
  fallback?: string;
  onNotFound?: (path: string) => void;
}

export class Router {
  private readonly routes = new Map<string, RouteHandler>();
  private readonly fallback: string;
  private readonly onNotFound: ((path: string) => void) | undefined;
  private readonly popstate = (): void => {
    void this.resolve();
  };

  constructor(options: RouterOptions = {}) {
    this.fallback = options.fallback ?? '/';
    this.onNotFound = options.onNotFound;
  }

  /** Registra o handler de um caminho literal. Um handler por rota (§3 do HYBRID). */
  on(path: string, handler: RouteHandler): this {
    this.routes.set(path, handler);
    return this;
  }

  /** `pushState` (nova entrada no histórico) e renderiza. */
  navigate(path: string, replace = false): void {
    const current = `${globalThis.location?.pathname ?? '/'}`;
    if (current === path) {
      void this.resolve();
      return;
    }
    if (replace) {
      globalThis.history?.replaceState({}, '', path);
    } else {
      globalThis.history?.pushState({}, '', path);
    }
    void this.resolve();
  }

  /** Renderiza a rota corrente. `popstate` (voltar/avançar) chama isto. */
  async resolve(): Promise<void> {
    const path = globalThis.location?.pathname ?? this.fallback;
    const handler = this.routes.get(path);
    if (handler) {
      await handler(path);
      return;
    }
    if (this.onNotFound) {
      this.onNotFound(path);
    }
  }

  start(): void {
    globalThis.addEventListener?.('popstate', this.popstate, { passive: true });
    void this.resolve();
  }

  stop(): void {
    globalThis.removeEventListener?.('popstate', this.popstate);
  }
}
